import { Button, Divider, InteractionTag, InteractionTagPrimary, InteractionTagSecondary, Link, MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { DragEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { AttachmentSourceType, GptStatus, GptTypes, IAttachment } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { updateActiveConversationAction } from '../../store/actions/conversationActions';
import { useConversations, useFeature } from '../../store/hooks';
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_UploadDocument, UserInteractionEventProperties } from '../../store/models/customEvents';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import { FileTypeIcon } from '../Common/FileTypeIcon';
import { LexicalEditor, LexicalEditorRef } from '../Common/LexicalEdit/LexicalEditor';
import { AttachmentButton } from './AttachmentButton';
import ImprovePromptButton from './ImprovePromptButton';
import ModelSelection from './ModelSelection';
import { PluginsMenu } from './PluginsMenu';
import SendButton from './SendButton';
import './UserQuery.scss';

export interface UserPromptProps {
    conversationStarterQuery: () => string;
    canSubmit: boolean;
    completionInProgress: boolean;
    uploadingFiles: string[];
    onSendQuery: (query: string, plugins: string[], modelName: string) => boolean | Promise<boolean>;
    onUploadDocumentAdd: (files: FileList, source: AttachmentSourceType) => void;
    onUploadDocumentRemove: (filename: string) => void | Promise<void>;
    hideModelSelection: boolean;
    restoreLastQueryTrigger?: number;
    inputValue?: string;
    onInputChange?: (value: string) => void;
    clearEditorTrigger?: number;
    restoreReason?: 'error' | 'stop';
    isEditingPrompt?: boolean;
}

// manages the user query input, including the attachment uploading and remove, and the send button
// attachment button is used within this component and events from the child component are managed here centrally
export const UserQuery: FC<UserPromptProps> = (props: UserPromptProps) => {
    const chatFeature = useFeature('chat');
    const maxMessageLength = chatFeature.conversation?.maxMessageLength;
    const maxConversationLength = chatFeature.conversation?.maxConversationLength;
    const { conversationStarterQuery, canSubmit, completionInProgress, uploadingFiles, onSendQuery, onUploadDocumentAdd, onUploadDocumentRemove, hideModelSelection, inputValue, onInputChange, clearEditorTrigger, restoreReason, isEditingPrompt } = props;
    const { activeChat, dispatch } = useConversations();
    const [internalQuery, setInternalQuery] = useState<string>('');
    const [lastQuery, setLastQuery] = useState<string>(''); // Store last sent query
    const [activePlugins, setActivePlugins] = useState<string[]>([]);
    const [attachments, setAttachments] = useState<IAttachment[]>([]);
    const [removingAttachments, setRemovingAttachments] = useState<Set<string>>(new Set());
    const [isUploadAttachmentEnabled, setisUploadAttachmentEnabled] = useState<boolean>(false);
    const [isDriveFileDownloading, setIsDriveFileDownloading] = useState<boolean>(false);
    const isAttachmentUploading = isDriveFileDownloading || uploadingFiles.length > 0;
    const [warningMessage, setWarningMessage] = useState<WarningMessage | undefined>(undefined);
    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const { t } = useTranslation();
    const { activeGpt } = useConversations();
    const { userProfile } = useUserContext();
    const [chosenModel, setChosenModel] = useState<string>('');
    const { dispatchNotification } = useNotification();


    // Only use internal state for editor content
    const [editorQuery, setEditorQuery] = useState<string>('');
    const editorRef = useRef<LexicalEditorRef>(null);
    const inputBoxRef = useRef<HTMLDivElement>(null);
    const lastEditorContentRef = useRef<string>('');
    const restoreTriggerRef = useRef<number>(0);
    const [restoreMessage, setRestoreMessage] = useState<string | undefined>(undefined);
    const [maxInputBoxHeight, setMaxInputBoxHeight] = useState<number | undefined>(undefined);

    const canSend = useMemo(() => canSubmit && editorQuery.trim().length > 0, [canSubmit, editorQuery]);
    const nbLines = useMemo(() => editorQuery?.split('\n').length ?? 1, [editorQuery]);

    const handleClearEditor = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.clearContent();
            setEditorQuery('');
            lastEditorContentRef.current = '';
        }
        setRestoreMessage(undefined);
    }, []);

    // Clear editor when trigger changes
    useEffect(() => {
        if (clearEditorTrigger !== undefined && clearEditorTrigger > 0) {
            handleClearEditor();
        }
    }, [clearEditorTrigger, handleClearEditor]);

    // Restore editor content when inputValue changes (used for restoring currentQuery after file upload)
    useEffect(() => {
        if (inputValue && inputValue !== editorQuery && editorRef.current) {
            editorRef.current.setContent(inputValue);
            setEditorQuery(inputValue);
            lastEditorContentRef.current = inputValue;
        }
    }, [inputValue]);

    // Reset processing state when completion is finished
    useEffect(() => {
        if (!completionInProgress && isProcessing) {
            setIsProcessing(false);
        }
    }, [completionInProgress, isProcessing]);

    useEffect(() => {
        // set a css variable to the number of lines
        document.documentElement.style.setProperty('--userquery-nb-lines', nbLines.toString());
    }, [nbLines]);

    // Compute maximum allowed input box height so the prompt box grows until the footer hits bottom of viewport
    const computeMaxInputBoxHeight = useCallback(() => {
        // Compute footer height dynamically instead of using a magic constant.
        // This ensures the available space calculation matches the actual footer size
        // for both new (welcome) and existing conversations.
        let footerReserve = 80;
        try {
            const footerEl = document.querySelector('.footer-container-margin') || document.querySelector('.footer-no-bottom-gap') || document.querySelector('footer');
            if (footerEl) {
                const fRect = (footerEl as HTMLElement).getBoundingClientRect();
                // If the footer is positioned normally, its height is the reserve.
                // Add a small safety margin of 24px.
                footerReserve = Math.ceil(fRect.height) + 30;
            }
        } catch (e) {
            // Keep default if anything goes wrong
            footerReserve = 80;
        }
        if (!inputBoxRef.current) {
            return setMaxInputBoxHeight(undefined);
        }
        const rect = inputBoxRef.current.getBoundingClientRect();

        // Determine if conversation is new (no prompts/messages yet)
        const isNewConversation = !(activeChat && activeChat.prompts && activeChat.prompts.length > 0);

        if (isNewConversation) {
            // available space from the top of the input-box to bottom of viewport minus footer reserve
            const available = window.innerHeight - rect.top - footerReserve;
            // clamp to reasonable min/max to avoid tiny/huge values
            const min = 76; // matches the input min-height calculation
            // For a new conversation, let the input-box extend to the bottom of the viewport
            const maxCap = Math.max(min, available);
            setMaxInputBoxHeight(maxCap);
            try { console.debug('[UserQuery] new conversation maxInputBoxHeight=', maxCap, 'available=', available, 'min=', min); } catch {}
        } else {
            // existing conversation: cap the prompt expansion to at most 1/3 of the viewport height
            const cap = Math.max(76, Math.floor(window.innerHeight / 3));
            setMaxInputBoxHeight(cap);
        }
    }, [inputBoxRef, activeChat]);

    useEffect(() => {
        // Recompute when window resizes / scrolls or when attachments/activeChat state changes (affects layout)
        computeMaxInputBoxHeight();
        const onResize = () => computeMaxInputBoxHeight();
        const onScroll = () => computeMaxInputBoxHeight();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll);
        };
    }, [computeMaxInputBoxHeight, attachments.length, activeChat]);

    // Log when the input-box actual height reaches the computed max height
    useEffect(() => {
        if (!inputBoxRef.current || !maxInputBoxHeight) return;
        try {
            const el = inputBoxRef.current;
            const check = () => {
                try {
                    const rect = el.getBoundingClientRect();
                    console.debug('[UserQuery] inputBox rect height=', rect.height, 'maxInputBoxHeight=', maxInputBoxHeight);
                    if (Math.abs(rect.height - maxInputBoxHeight) <= 1) {
                        console.debug('[UserQuery] inputBox reached max height (editor full)');
                    }
                } catch (e) {}
            };
            check();
            const ro = new ResizeObserver(check);
            ro.observe(el);
            return () => ro.disconnect();
        } catch (e) {
            // noop
        }
    }, [inputBoxRef.current, maxInputBoxHeight]);

    useEffect(() => {
        if (activeChat && activeGpt) {
            setAttachments(activeChat.attachments ?? []);

            const maxAttachments = activeGpt?.attachment?.maxFiles ?? 1;
            setisUploadAttachmentEnabled(attachments.length < maxAttachments);

            if (activeChat.prompts && maxConversationLength && activeChat.prompts.length > maxConversationLength) {
                setWarningMessage({
                    text: t('conversation.tooManyMessages'),
                    link: 'https://wiki.syensqo.com/pages/viewpage.action?pageId=899156406',
                });
            }
            if (activeChat.gpt?.type === GptTypes.PersonalGpt) {
                // if a conversation has started before the last modification of the personal GPT, send a warning message
                if (activeGpt?.lastUpdateOn && activeChat.startedOn < activeGpt.lastUpdateOn) {
                    setWarningMessage({
                        text: t('personalGpt.warnings.oldConversation'),
                    });
                }

                // warning message about running status overrides the possible warning message about old conversation
                if (activeGpt?.status === GptStatus.Running) {
                    setWarningMessage({
                        text: t('personalGpt.warnings.statusRunning'),
                    });
                }
            }
        }
    }, [activeChat, activeGpt, attachments, maxConversationLength, t]);

    // Only set editor content programmatically for starter, restore, etc.
    useEffect(() => {
        const starterText = conversationStarterQuery();
        if (starterText && editorRef.current) {
            editorRef.current.setContent(starterText);
            setEditorQuery(starterText);
            lastEditorContentRef.current = starterText;
            setRestoreMessage(undefined);
            // Position cursor at the end after setting starter content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    }, [conversationStarterQuery]);

    // Restore last query when triggered
    const onChangeQuery = (newQuery: string) => {
        const message: WarningMessage | undefined = maxMessageLength && newQuery.length >= maxMessageLength ? { text: t('conversation.tooLongMessage') } : undefined;
        setWarningMessage(message);
        setEditorQuery(newQuery);
        lastEditorContentRef.current = newQuery;
        setRestoreMessage(undefined);
        
        // Notify parent of the change so it can update its state
        if (onInputChange) {
            onInputChange(newQuery);
        }
    };

    // Function specifically for external updates (like voice recognition)
    const onChangeQueryExternal = (newQuery: string) => {
        const message: WarningMessage | undefined = maxMessageLength && newQuery.length >= maxMessageLength ? { text: t('conversation.tooLongMessage') } : undefined;
        setWarningMessage(message);
        setEditorQuery(newQuery);
        lastEditorContentRef.current = newQuery;
    setRestoreMessage(undefined);

        // Update the LexicalEditor content
        if (editorRef.current) {
            editorRef.current.setContent(newQuery);
            // Position cursor at the end after setting content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    };

    // Modified: Only clear the query if onSendQuery resolves successfully; don't clear on error
    // Helper type guard for Promise
    function isPromise<T = unknown>(value: unknown): value is Promise<T> {
        return !!value && typeof value === 'object' && typeof (value as Promise<T>).then === 'function';
    }

    const handleSendCompletion = useCallback(
        (success: boolean | undefined, sentQuery: string) => {
            setIsProcessing(false);

            if (success) {
                setLastQuery('');
                return;
            }

            const message = t('conversation.inputRestoredAfterError', 'Message restored after an error. You can edit and resend.');
            setRestoreMessage(message);
            if (editorRef.current) {
                editorRef.current.setContent(sentQuery);
                setTimeout(() => editorRef.current?.focusAtEnd(), 0);
            }
            setEditorQuery(sentQuery);
            lastEditorContentRef.current = sentQuery;
        },
        [handleClearEditor, t],
    );

    const handleSendQuery = useCallback(() => {
        if (canSend) {
            const sentQuery = editorQuery;
            setLastQuery(sentQuery);
            setIsProcessing(true);
            setRestoreMessage(undefined);
            handleClearEditor();

            const result = onSendQuery(sentQuery, activePlugins, chosenModel);

            if (isPromise(result)) {
                result
                    .then((success) => {
                        handleSendCompletion(success, sentQuery);
                    })
                    .catch((error) => {
                        console.error('Error sending query:', error);
                        handleSendCompletion(false, sentQuery);
                    });
            } else {
                handleSendCompletion(result, sentQuery);
            }
        }
    }, [canSend, editorQuery, onSendQuery, activePlugins, chosenModel, handleSendCompletion]);

    // Handler to restore last query when stop responding is clicked
    const handleStopResponding = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.setContent(lastQuery);
            setEditorQuery(lastQuery);
            // Position cursor at the end after restoring content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    }, [lastQuery]);


    useEffect(() => {
        if (
            props.restoreLastQueryTrigger !== undefined &&
            props.restoreLastQueryTrigger > 0 &&
            props.restoreLastQueryTrigger !== restoreTriggerRef.current
        ) {
            restoreTriggerRef.current = props.restoreLastQueryTrigger;
            handleStopResponding();

            if (restoreReason === 'stop') {
                setRestoreMessage(t('conversation.inputRestoredAfterStop', 'Message restored because the response was stopped.'));
            } else if (restoreReason === 'error') {
                setRestoreMessage(t('conversation.inputRestoredAfterError', 'Message restored after an error. You can edit and resend.'));
            } else {
                setRestoreMessage(undefined);
            }
        }
    }, [props.restoreLastQueryTrigger, handleStopResponding, restoreReason, t]);

    const activePluginsChanged = (plugins: string[]) => {
        setActivePlugins(plugins);
    };

    const onUploadDocumentClick = useCallback(
        (files: FileList, source: AttachmentSourceType) => {
            onUploadDocumentAdd(files, source);
            appInsights.trackEvent({
                name: EventName_UserInteraction,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username,
                    interactionType: EventProperty_Value_UploadDocument,
                    isSuccess: true,
                } as UserInteractionEventProperties,
            });
        },
        [onUploadDocumentAdd, userProfile],
    );

    // Handle image paste
    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            // Check if there are image items in the clipboard
            const items = event.clipboardData?.items;
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1 && isUploadAttachmentEnabled && !completionInProgress) {
                        // Get the image as blob
                        const blob = items[i].getAsFile();
                        if (blob) {
                            // Create a FileList-like object for the onUploadDocumentClick function
                            const dT = new DataTransfer();
                            // Generate a name for the file using timestamp to ensure uniqueness
                            const fileName = `pasted-image-${new Date().getTime()}.png`;
                            const file = new File([blob], fileName, { type: blob.type });
                            dT.items.add(file);

                            // Use the existing upload document function
                            onUploadDocumentClick(dT.files, AttachmentSourceType.LocalDrive);

                            // Prevent default paste behavior for images
                            event.preventDefault();
                            break;
                        }
                    }
                }
            }
        },
        [onUploadDocumentClick, isUploadAttachmentEnabled, completionInProgress],
    );

    // Handle key down events for Lexical editor
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter' && event.shiftKey === false) {
                // Toujours empêcher le comportement par défaut pour Enter seul
                event.preventDefault();
                
                // Envoyer la requête seulement si les conditions sont remplies
                if (canSend && editorQuery && editorQuery.trim().length > 0 && editorQuery !== '\n') {
                    handleSendQuery();
                }
            }
            // Shift + Enter permet le retour à la ligne (comportement par défaut)
        },
        [canSend, handleSendQuery, editorQuery],
    );

    // Drag and drop event handlers
    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDraggingOver(false);

            if (event.dataTransfer.files && event.dataTransfer.files.length > 0 && isUploadAttachmentEnabled && !completionInProgress) {
                onUploadDocumentClick(event.dataTransfer.files, AttachmentSourceType.LocalDrive);
            }
        },
        [onUploadDocumentClick, isUploadAttachmentEnabled, completionInProgress],
    );

    // prevents duplicate delete calls if the user clicks the dismiss icon multiple times before the attachment is actually removed
    const handleRemoveAttachment = useCallback(
        async (filename: string) => {
            if (removingAttachments.has(filename)) {
                return;
            }
            setRemovingAttachments((prev) => new Set(prev).add(filename));
            try {
                await onUploadDocumentRemove(filename);
            } finally {
                setRemovingAttachments((prev) => {
                    const updated = new Set(prev);
                    updated.delete(filename);
                    return updated;
                });
            }
        },
        [onUploadDocumentRemove, removingAttachments],
    );

    const handleModelChange = useCallback((model: string) => {
        setChosenModel(model);
    }, []);

    const handleImprovePrompt = useCallback(async () => {
        if (!editorQuery.trim()) {
            dispatchNotification('Please enter a prompt.', 'warning');
            return;
        }
        setIsProcessing(true);
        try {
            const { ServiceFactory } = await import('../../EnterpriseGPTApi/ServiceFactory');
            const { ImprovePromptRequest } = await import('../../EnterpriseGPTApi/EnterpriseGPTApiV1Client');
            const apiClient = ServiceFactory.EnterpriseGPTApiClient();
            const request = new ImprovePromptRequest({ prompt: editorQuery });
            const result = await apiClient.improvePrompt(request);
            if (result?.improvedPrompt) {
                const improved = result.improvedPrompt;
                setEditorQuery(improved);
                lastEditorContentRef.current = improved;
                if (editorRef.current) {
                    editorRef.current.setContent(improved);
                    setTimeout(() => editorRef.current?.focusAtEnd(), 0);
                }
                dispatchNotification('Improved Prompt', 'success');
            }
        } catch (error: any) {
            console.error('Error improving prompt:', error);
            console.error('Error response:', error?.response);
            console.error('Error message:', error?.message);
            const errorMsg = error?.response?.data?.error || error?.message || 'Unknown error';
            dispatchNotification(`Error: ${errorMsg}`, 'error');
        } finally {
            setIsProcessing(false);
        }
    }, [editorQuery, dispatchNotification]);

    // Handle click on input-box to focus editor (especially important for mobile to show keyboard)
    const handleInputBoxClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        // Only focus if clicking directly on the input-box background (not on buttons or other interactive elements)
        if (event.target === event.currentTarget || (event.target as HTMLElement).classList.contains('content')) {
            editorRef.current?.focus();
        }
    }, []);

    // Calculate the appropriate mode for SendButton
    const getSendButtonMode = useCallback(() => {
        if (isProcessing || completionInProgress) {
            return 'processing';
        }
        return editorQuery.length > 0 ? 'typing' : 'ready';
    }, [isProcessing, completionInProgress, editorQuery.length]);

    // Focus the editor as soon as the page is rendered, but only if there is no starter text, last query, or inputValue to set
    useEffect(() => {
        if (
            editorRef.current &&
            !internalQuery && // Only focus if there is no content
            !inputValue
        ) {
            editorRef.current.focus();
        }
    }, []);
    
    return (
        <div className='userQuery'>
            <div className='input-container'>
                <div
                    ref={inputBoxRef}
                    className={`input-box ${isDraggingOver ? 'dragging-over' : ''}`}
                    onClick={handleInputBoxClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={maxInputBoxHeight ? { maxHeight: `${maxInputBoxHeight}px` } : undefined}
                >
                    <div className='content'>
                        {warningMessage && (
                            <MessageBar intent='warning' className='warning'>
                                <MessageBarBody>
                                    <MessageBarTitle>Warning</MessageBarTitle>
                                    {warningMessage.text}
                                    {warningMessage.link && (
                                        <Link href={warningMessage.link} target='_blank'>
                                            Learn more
                                        </Link>
                                    )}
                                </MessageBarBody>
                            </MessageBar>
                        )}

                        {restoreMessage && (
                            <MessageBar intent='info' className='restore-message'>
                                <MessageBarBody>{restoreMessage}</MessageBarBody>
                            </MessageBar>
                        )}

                        <LexicalEditor
                            ref={editorRef}                            
                            className='lexicaleditor'
                            placeholder='Ask anything...'
                            maxLength={maxMessageLength}
                            onChange={onChangeQuery}
                            onPaste={handlePaste}
                            onKeyDown={handleKeyDown}
                        />

                        {(isAttachmentUploading || attachments.length > 0) && <Divider />}
                        {(isAttachmentUploading || attachments.length > 0) && (
                            <div className='attachmentsContainer'>
                                {
                                    // display the attachments added by the user
                                    attachments.map((a) => (
                                        <InteractionTag key={a.name} disabled={completionInProgress || removingAttachments.has(a.name!)}>
                                            <InteractionTagPrimary icon={<FileTypeIcon filename={a.name!} />} hasSecondaryAction>
                                                {a.name}
                                            </InteractionTagPrimary>
                                            <InteractionTagSecondary aria-label='dismiss' onClick={() => handleRemoveAttachment(a.name!)} />
                                        </InteractionTag>
                                    ))
                                }
                                {uploadingFiles.map((name) => (
                                    <InteractionTag key={`uploading_${name}`}>
                                        <InteractionTagPrimary icon={<Spinner size='extra-tiny' />}>{`${name} - ${t('conversation.attachments.uploading')}`}</InteractionTagPrimary>
                                    </InteractionTag>
                                ))}
                                {isDriveFileDownloading && (
                                    <InteractionTag>
                                        <InteractionTagPrimary icon={<Spinner size='extra-tiny' />}>{t('conversation.attachments.uploading')}</InteractionTagPrimary>
                                    </InteractionTag>
                                )}
                            </div>
                        )}
                        {isDraggingOver && (
                            <div className='drop-overlay'>
                                <div className='drop-message'>Drop files here to upload</div>
                            </div>
                        )}
                    </div>
                    
                    <div className='button-row'>
                        <div className='button-group-left'>
                            {
                                // display the feature highlight popup for multiple document upload, if enabled
                                activeGpt?.attachment && activeGpt.attachment.isEnabled &&
                                activeGpt?.type !== GptTypes.ImageGenerationGpt &&
                                (
                                    <>
                                        <div className="feature-popup-buttons">
                                            <ImprovePromptButton onClick={handleImprovePrompt} disabled={completionInProgress} />
                                        </div>
                                        <div className="feature-popup-buttons">
                                            <FeatureHighlightPopup
                                                id={'multipleDocumentUpload'}
                                                header={t('featureHighlights.header')}
                                                title={t('featureHighlights.multipleDocumentUpload.title')}
                                                content={t('featureHighlights.multipleDocumentUpload.content')}
                                                link={t('featureHighlights.multipleDocumentUpload.link')}
                                            >
                                                <AttachmentButton
                                                    enabled={isUploadAttachmentEnabled && !completionInProgress && !isEditingPrompt && !isAttachmentUploading}
                                                    onClick={onUploadDocumentClick}
                                                    setIsDriveFileDownloading={setIsDriveFileDownloading}
                                                    onImprovePrompt={handleImprovePrompt}
                                                />
                                            </FeatureHighlightPopup>
                                        </div>
                                    </>
                                )
                            }
                            {
                                // allow the users to activate specific plugins (e.g. web search)
                                <PluginsMenu plugins={activeGpt?.plugins ?? []} pluginsChanged={activePluginsChanged} />
                            }
                        </div>                        

                        <div className='button-group-right'>
                            <ModelSelection disabled={hideModelSelection} changeModel={handleModelChange} />
                            <SendButton mode={getSendButtonMode()} onClick={() => handleSendQuery()} onChangeQuery={onChangeQueryExternal} onSendQuery={handleSendQuery} currentQuery={editorQuery} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface WarningMessage {
    text: string;
    link?: string;
}






